import React, { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import { type FilterPreset } from '@/components/shared/filters';
import { useCustomers } from '../../api/customers/use-customers';
import { usePermissions } from '@/hooks/usePermissions.ts';
import { formatCurrency } from '@/lib/format';
import { useSnackbar } from 'notistack';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { useExportTransactions } from '../../api/export/use-export';
import {
    useAllTransactions,
    useDeleteTransaction,
    useUpdateTransaction,
} from '@/api/transactions/use-crm-transactions.tsx';
import { Transaction } from '@/models/transaction';
import { InlineEdit } from '@/components/ui/inline-edit';
import { useUrlState } from '@/hooks/shared';
import { useConfirm } from '@/hooks/useConfirm';
import { usePromptDialog } from '@/hooks/usePromptDialog';
import {
    AlertTriangle,
    Calendar,
    Check,
    CreditCard,
    Gift,
    TrendingDown,
    TrendingUp,
    User,
    X,
    XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { Link } from 'react-router-dom';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogFooter,
} from '@/components/ui/dialog';

const getCreatedByLabel = (transaction: Transaction) => {
    const tx = transaction as Transaction & {
        created_by_type?: string;
        created_by_name?: string;
        metadata?: Record<string, any>;
    };

    if (tx.created_by_type === 'customer') {
        return 'customer';
    }

    if (tx.created_by_type === 'crm_user') {
        return tx.created_by_name || 'CRM User';
    }

    const source = tx.metadata?.source;
    if (source && source !== 'crm_manual') {
        return 'customer';
    }

    return tx.created_by_name || 'CRM User';
};

/** `type` isn't covered by StatusBadge's status kinds, so it keeps its own badge. */
const getTypeBadge = (type: string) => {
    const typeConfig = {
        deposit: {
            color: 'bg-success/10 text-success border-success/20',
            label: 'Deposit',
            icon: <TrendingUp className="h-3 w-3" />,
        },
        withdraw: {
            color: 'bg-destructive/10 text-destructive border-destructive/20',
            label: 'Withdrawal',
            icon: <TrendingDown className="h-3 w-3" />,
        },
        withdrawal: {
            color: 'bg-destructive/10 text-destructive border-destructive/20',
            label: 'Withdrawal',
            icon: <TrendingDown className="h-3 w-3" />,
        },
        bonus: {
            color: 'bg-warning/10 text-warning border-warning/20',
            label: 'Bonus',
            icon: <Gift className="h-3 w-3" />,
        },
    };

    const config = typeConfig[type as keyof typeof typeConfig] || {
        color: 'bg-muted text-muted-foreground border-border',
        label: type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Transaction',
        icon: <CreditCard className="h-3 w-3" />,
    };
    return (
        <Badge className={`${config.color} flex items-center gap-1`}>
            {config.icon}
            {config.label}
        </Badge>
    );
};

const getAmountColor = (amount: number) => (amount >= 0 ? 'text-success' : 'text-destructive');

const TransactionsPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const confirm = useConfirm();
    const promptDialog = usePromptDialog();
    const canEditTransactions =
        hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.EDIT) || false;

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
    } = useUrlState({
        sort: { key: 'created_at', direction: 'desc' },
        pagination: { page: 1, pageSize: 25 },
    });

    // State for details modal
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    // API hooks - only pass filters that have values, like LeadsPage
    const { data: customers } = useCustomers();
    const exportTransactionsMutation = useExportTransactions(
        () => {
            enqueueSnackbar('Transactions exported successfully', { variant: 'success' });
        },
        (error: Error) => {
            console.error('Export failed:', error);
            enqueueSnackbar('Failed to export transactions', { variant: 'error' });
        }
    );

    const handleExport = () => {
        exportTransactionsMutation.mutate({
            format: 'csv',
            filters: { ...filters },
        });
    };

    const { data: transactionsData, isLoading } = useAllTransactions({
        page: pagination.page,
        per_page: pagination.pageSize,
        sort_by: sort.key,
        sort_direction: sort.direction,
        ...Object.fromEntries(
            Object.entries(filters).filter(
                ([, value]) =>
                    value !== undefined && value !== '' && value !== null && value !== 'all'
            )
        ),
    });
    const updateTransactionMutation = useUpdateTransaction();
    const deleteTransactionMutation = useDeleteTransaction();

    // Extract data
    const transactions = Array.isArray(transactionsData?.data) ? transactionsData.data : [];
    const totalTransactions = transactionsData?.meta?.total || 0;

    // Calculate statistics
    const transactionStats = {
        totalTransactions,
        totalDeposits: transactions.reduce(
            (sum: number, transaction: Transaction) =>
                transaction.type === 'deposit' ? sum + Math.abs(Number(transaction.amount)) : sum,
            0
        ),
        totalWithdrawals: transactions.reduce(
            (sum: number, transaction: Transaction) =>
                transaction.type === 'withdraw' ? sum + Math.abs(Number(transaction.amount)) : sum,
            0
        ),
        pendingTransactions: transactions.filter(
            (transaction: Transaction) => transaction.status === 'pending'
        ).length,
        completedTransactions: transactions.filter(
            (transaction: Transaction) => transaction.status === 'completed'
        ).length,
        failedTransactions: transactions.filter(
            (transaction: Transaction) => transaction.status === 'failed'
        ).length,
    };

    // Handle filter changes
    const handleFilterChange = (newFilters: any) => {
        updateFilters(newFilters);
    };

    const handleClearFilters = () => {
        clearFilters();
    };

    /** Drives the empty state: "no matches" reads differently from "nothing yet". */
    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    const handleSort = (key: keyof Transaction) => {
        toggleSort(key as string);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    // Define filter fields for the GenericDataTable
    const filterFields = useMemo(
        () => [
            {
                key: 'search',
                label: 'Search',
                type: 'text' as const,
                placeholder: 'Search transactions...',
            },
            {
                key: 'customer_id',
                label: 'Customer',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All Customers' },
                    ...(customers?.map(customer => ({
                        value: customer.id,
                        label: customer.name,
                    })) || []),
                ],
            },
            {
                key: 'type',
                label: 'Type',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All types' },
                    { value: 'deposit', label: 'Deposit' },
                    { value: 'withdraw', label: 'Withdrawal' },
                    { value: 'bonus', label: 'Bonus' },
                ],
            },
            {
                key: 'status',
                label: 'Status',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All statuses' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'failed', label: 'Failed' },
                    { value: 'cancelled', label: 'Cancelled' },
                ],
            },
            {
                key: 'payment_method',
                label: 'Payment Method',
                type: 'text' as const,
                placeholder: 'Filter by payment method...',
            },
        ],
        [customers]
    );

    // Save filter preset
    const handleSavePreset = (preset: Omit<FilterPreset, 'id'>) => {
        enqueueSnackbar(`Filter preset "${preset.name}" saved`, { variant: 'success' });
    };

    // Filter presets
    const filterPresets: FilterPreset[] = useMemo(
        () => [
            {
                id: 'deposits',
                name: 'Deposits',
                description: 'All deposit transactions',
                isDefault: true,
                filters: {
                    type: 'deposit',
                },
            },
            {
                id: 'withdrawals',
                name: 'Withdrawals',
                description: 'All withdrawal transactions',
                isDefault: true,
                filters: {
                    type: 'withdraw',
                },
            },
            {
                id: 'bonuses',
                name: 'Bonuses',
                description: 'All bonus transactions',
                isDefault: true,
                filters: {
                    type: 'bonus',
                },
            },
            {
                id: 'pending-transactions',
                name: 'Pending',
                description: 'Transactions awaiting processing',
                isDefault: true,
                filters: {
                    status: 'pending',
                },
            },
            {
                id: 'completed-transactions',
                name: 'Completed',
                description: 'Successfully processed transactions',
                isDefault: true,
                filters: {
                    status: 'completed',
                },
            },
            {
                id: 'failed-transactions',
                name: 'Failed',
                description: 'Failed transactions',
                isDefault: true,
                filters: {
                    status: 'failed',
                },
            },
            {
                id: 'today-transactions',
                name: "Today's Transactions",
                description: 'Transactions processed today',
                isDefault: true,
                filters: {
                    from_date: new Date().toISOString().split('T')[0],
                    to_date: new Date().toISOString().split('T')[0],
                },
            },
        ],
        []
    );

    // Define columns for the GenericDataTable
    const columns: Column<Transaction>[] = useMemo(
        () => [
            {
                id: 'reference',
                label: 'Reference',
                key: 'reference_id',
                sortable: true,
                width: '150px',
                render: (_, transaction) => (
                    <span className="font-mono text-sm">{transaction.reference_id}</span>
                ),
            },
            {
                id: 'customer',
                label: 'Customer',
                key: 'customer_id',
                sortable: true,
                width: '200px',
                render: (_, transaction) => (
                    <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {transaction.customer?.id ? (
                            <Link
                                to={`/customers/${transaction.customer.id}`}
                                className="text-primary hover:underline"
                                onClick={event => event.stopPropagation()}
                            >
                                {transaction.customer?.name || 'N/A'}
                            </Link>
                        ) : (
                            <span>{transaction.customer?.name || 'N/A'}</span>
                        )}
                    </div>
                ),
            },
            {
                id: 'type',
                label: 'Type',
                key: 'type',
                sortable: true,
                width: '120px',
                render: (_, transaction) => getTypeBadge(transaction.type),
            },
            {
                id: 'amount',
                label: 'Amount',
                key: 'amount',
                sortable: true,
                width: '150px',
                render: (_, transaction) => (
                    <span
                        className={`font-medium tabular-figures ${getAmountColor(Number(transaction.amount))}`}
                    >
                        {formatCurrency(
                            Math.abs(Number(transaction.amount)),
                            transaction.currency || 'USD'
                        )}
                    </span>
                ),
            },
            {
                id: 'status',
                label: 'Status',
                key: 'status',
                render: (_, transaction) => (
                    <StatusBadge status={transaction.status} kind="transaction" />
                ),
            },
            {
                id: 'payment_method',
                label: 'Payment Method',
                key: 'payment_method',
                sortable: true,
                width: '170px',
                render: (_, transaction) => <span>{transaction.payment_method || 'N/A'}</span>,
            },
            {
                id: 'created_by',
                label: 'Created By',
                key: 'created_by',
                sortable: false,
                width: '170px',
                render: (_, transaction) => <span>{getCreatedByLabel(transaction)}</span>,
            },
            {
                id: 'date',
                label: 'Date',
                key: 'created_at',
                sortable: true,
                width: '180px',
                render: (_, transaction) => (
                    <DateTimeFormatter
                        date={transaction.created_at}
                        showIcon={true}
                        icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
                        className="text-sm"
                    />
                ),
            },
        ],
        []
    );

    const handleViewDetails = (transaction: Transaction) => {
        setSelectedTransaction(transaction);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedTransaction(null);
    };

    const handleDeleteTransaction = async (transactionId: string) => {
        const ok = await confirm({
            title: 'Delete transaction?',
            description: 'This action cannot be undone.',
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;

        try {
            await deleteTransactionMutation.mutateAsync(transactionId);
            enqueueSnackbar('Transaction deleted successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to delete transaction', { variant: 'error' });
        }
    };

    const handleApproveTransaction = async (transactionId: string) => {
        const ok = await confirm({
            title: 'Approve transaction?',
            description: 'The transaction will be marked as completed.',
            confirmLabel: 'Approve',
        });
        if (!ok) return;

        try {
            await updateTransactionMutation.mutateAsync({
                id: transactionId,
                status: 'completed',
            });
            enqueueSnackbar('Transaction approved successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to approve transaction', { variant: 'error' });
        }
    };

    const handleRejectTransaction = async (transactionId: string) => {
        const reason = await promptDialog({
            title: 'Reject transaction',
            label: 'Reason',
            placeholder: 'Why is this transaction being rejected?',
            required: true,
        });
        if (!reason) {
            return;
        }

        try {
            await updateTransactionMutation.mutateAsync({
                id: transactionId,
                status: 'cancelled',
                rejection_reason: reason,
            });
            enqueueSnackbar('Transaction rejected successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to reject transaction', { variant: 'error' });
        }
    };

    const handleUpdateField = async (
        transactionId: string,
        field: string,
        value: string | number
    ) => {
        try {
            const updatedTransaction = await updateTransactionMutation.mutateAsync({
                id: transactionId,
                [field]: value,
            });
            enqueueSnackbar('Transaction updated successfully', { variant: 'success' });
            // Update the selected transaction in the dialog if it's the same one
            if (selectedTransaction && selectedTransaction.id === transactionId) {
                setSelectedTransaction(updatedTransaction);
            }
        } catch (error) {
            enqueueSnackbar('Failed to update transaction', { variant: 'error' });
        }
    };

    return (
        <PageContainer fullHeight>
            <PageHeader title="Transactions" description="Manage all customer transactions" />

            <StatTileGrid className="shrink-0 xl:grid-cols-3 2xl:grid-cols-6">
                <StatTile
                    label="Total Transactions"
                    value={transactionStats.totalTransactions}
                    icon={CreditCard}
                    tone="brand"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Total Deposits"
                    value={formatCurrency(transactionStats.totalDeposits, 'USD')}
                    icon={TrendingUp}
                    tone="success"
                    emphasizeValue
                    isLoading={isLoading}
                />
                <StatTile
                    label="Total Withdrawals"
                    value={formatCurrency(transactionStats.totalWithdrawals, 'USD')}
                    icon={TrendingDown}
                    tone="destructive"
                    emphasizeValue
                    isLoading={isLoading}
                />
                <StatTile
                    label="Pending"
                    value={transactionStats.pendingTransactions}
                    icon={AlertTriangle}
                    tone="warning"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Completed"
                    value={transactionStats.completedTransactions}
                    icon={Check}
                    tone="success"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Failed"
                    value={transactionStats.failedTransactions}
                    icon={XCircle}
                    tone="destructive"
                    emphasizeValue
                    isLoading={isLoading}
                />
            </StatTileGrid>

            {/* Generic Data Table */}
            <GenericDataTable<Transaction>
                data={transactions}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={filters}
                filterFields={filterFields}
                presets={filterPresets}
                isLoading={isLoading}
                selectedItems={[]}
                onSelectionChange={() => {}}
                getItemId={transaction => transaction.id}
                sortConfig={{
                    key: sort.key as keyof Transaction,
                    direction: sort.direction,
                }}
                onSort={handleSort}
                totalItems={totalTransactions}
                totalPages={transactionsData?.meta?.last_page || 1}
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onExport={handleExport}
                showExportButton={hasPermission(
                    ModulePermission.TRANSACTIONS,
                    PermissionAction.EXPORT
                )}
                onRowClick={handleViewDetails}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onLoadPreset={preset => handleFilterChange(preset.filters)}
                onSavePreset={handleSavePreset}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage="No transactions found"
                loadingMessage="Loading transactions..."
                showRowClick={true}
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={CreditCard}
                        title="No transactions found"
                        description={
                            hasActiveFilters
                                ? 'No transaction matches the current filters. Try clearing them.'
                                : 'Transactions will appear here once customers make deposits or withdrawals.'
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                                    Clear filters
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />

            {/* Transaction Details Dialog */}
            <Dialog
                open={isDetailsModalOpen}
                onOpenChange={open => !open && handleCloseDetailsModal()}
            >
                <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
                    {selectedTransaction && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex flex-wrap items-center gap-3">
                                    Transaction Details
                                    {getTypeBadge(selectedTransaction.type)}
                                    <StatusBadge
                                        status={selectedTransaction.status}
                                        kind="transaction"
                                    />
                                </DialogTitle>
                            </DialogHeader>

                            <DialogBody>
                                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                                    {/* Transaction Information */}
                                    <div className="space-y-4">
                                        <div className="rounded-lg border border-border bg-muted/50 p-4">
                                            <h3 className="mb-3 font-medium text-foreground">
                                                Transaction Information
                                            </h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        ID:
                                                    </span>
                                                    <span className="font-mono text-sm font-medium text-foreground">
                                                        {selectedTransaction.id}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Reference:
                                                    </span>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {selectedTransaction.reference_id}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Amount:
                                                    </span>
                                                    <span
                                                        className={`text-sm font-medium tabular-figures ${getAmountColor(Number(selectedTransaction.amount))}`}
                                                    >
                                                        {formatCurrency(
                                                            Math.abs(
                                                                Number(selectedTransaction.amount)
                                                            ),
                                                            selectedTransaction.currency || 'USD'
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Payment Method:
                                                    </span>
                                                    <InlineEdit
                                                        value={selectedTransaction.payment_method}
                                                        onSave={value =>
                                                            handleUpdateField(
                                                                selectedTransaction.id,
                                                                'payment_method',
                                                                value
                                                            )
                                                        }
                                                        placeholder="No payment method"
                                                        className="text-sm font-medium text-foreground"
                                                        permissionModule={
                                                            ModulePermission.TRANSACTIONS
                                                        }
                                                    />
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Status:
                                                    </span>
                                                    <InlineEdit
                                                        value={selectedTransaction.status}
                                                        onSave={value =>
                                                            handleUpdateField(
                                                                selectedTransaction.id,
                                                                'status',
                                                                value
                                                            )
                                                        }
                                                        placeholder="Select status"
                                                        className="text-sm font-medium text-foreground"
                                                        permissionModule={
                                                            ModulePermission.TRANSACTIONS
                                                        }
                                                        type="select"
                                                        selectOptions={[
                                                            { value: 'pending', label: 'Pending' },
                                                            {
                                                                value: 'completed',
                                                                label: 'Completed',
                                                            },
                                                            { value: 'failed', label: 'Failed' },
                                                            {
                                                                value: 'cancelled',
                                                                label: 'Cancelled',
                                                            },
                                                        ]}
                                                    />
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Description:
                                                    </span>
                                                    <InlineEdit
                                                        value={selectedTransaction.description}
                                                        onSave={value =>
                                                            handleUpdateField(
                                                                selectedTransaction.id,
                                                                'description',
                                                                value
                                                            )
                                                        }
                                                        placeholder="No description"
                                                        className="text-sm font-medium text-foreground"
                                                        permissionModule={
                                                            ModulePermission.TRANSACTIONS
                                                        }
                                                    />
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Created By:
                                                    </span>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {getCreatedByLabel(selectedTransaction)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rounded-lg border border-border bg-muted/50 p-4">
                                            <h3 className="mb-3 font-medium text-foreground">
                                                Customer Information
                                            </h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Customer:
                                                    </span>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {selectedTransaction.customer?.id ? (
                                                            <Link
                                                                to={`/customers/${selectedTransaction.customer.id}`}
                                                                className="text-primary hover:underline"
                                                            >
                                                                {selectedTransaction.customer
                                                                    ?.name || 'N/A'}
                                                            </Link>
                                                        ) : (
                                                            selectedTransaction.customer?.name ||
                                                            'N/A'
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Email:
                                                    </span>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {selectedTransaction.customer?.email ||
                                                            'N/A'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Trading Account:
                                                    </span>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {selectedTransaction.trading_account
                                                            ?.base_currency || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Timestamps and Actions */}
                                    <div className="space-y-4">
                                        <div className="rounded-lg border border-border bg-muted/50 p-4">
                                            <h3 className="mb-3 font-medium text-foreground">
                                                Timestamps
                                            </h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Created:
                                                    </span>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {format(
                                                            new Date(
                                                                selectedTransaction.created_at
                                                            ),
                                                            'MMM dd, yyyy HH:mm'
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Updated:
                                                    </span>
                                                    <span className="text-sm font-medium text-foreground">
                                                        {format(
                                                            new Date(
                                                                selectedTransaction.updated_at
                                                            ),
                                                            'MMM dd, yyyy HH:mm'
                                                        )}
                                                    </span>
                                                </div>
                                                {selectedTransaction.approved_at && (
                                                    <div className="flex justify-between">
                                                        <span className="text-sm text-muted-foreground">
                                                            Approved:
                                                        </span>
                                                        <span className="text-sm font-medium text-foreground">
                                                            {format(
                                                                new Date(
                                                                    selectedTransaction.approved_at
                                                                ),
                                                                'MMM dd, yyyy HH:mm'
                                                            )}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedTransaction.rejected_at && (
                                                    <div className="flex justify-between">
                                                        <span className="text-sm text-muted-foreground">
                                                            Rejected:
                                                        </span>
                                                        <span className="text-sm font-medium text-foreground">
                                                            {format(
                                                                new Date(
                                                                    selectedTransaction.rejected_at
                                                                ),
                                                                'MMM dd, yyyy HH:mm'
                                                            )}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {selectedTransaction.approved_by && (
                                            <div className="rounded-lg border border-border bg-muted/50 p-4">
                                                <h3 className="mb-3 font-medium text-foreground">
                                                    Approval Information
                                                </h3>
                                                <div className="space-y-2">
                                                    <div className="flex justify-between">
                                                        <span className="text-sm text-muted-foreground">
                                                            Approved By:
                                                        </span>
                                                        <span className="text-sm font-medium text-foreground">
                                                            {selectedTransaction.approved_by}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {selectedTransaction.rejected_by && (
                                            <div className="rounded-lg border border-border bg-muted/50 p-4">
                                                <h3 className="mb-3 font-medium text-foreground">
                                                    Rejection Information
                                                </h3>
                                                <div className="space-y-2">
                                                    <div className="flex justify-between">
                                                        <span className="text-sm text-muted-foreground">
                                                            Rejected By:
                                                        </span>
                                                        <span className="text-sm font-medium text-foreground">
                                                            {selectedTransaction.rejected_by}
                                                        </span>
                                                    </div>
                                                    {selectedTransaction.rejection_reason && (
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-muted-foreground">
                                                                Reason:
                                                            </span>
                                                            <span className="text-sm font-medium text-foreground">
                                                                {
                                                                    selectedTransaction.rejection_reason
                                                                }
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="rounded-lg border border-border bg-muted/50 p-4">
                                            <h3 className="mb-3 font-medium text-foreground">
                                                Notes
                                            </h3>
                                            <InlineEdit
                                                value={selectedTransaction.notes}
                                                onSave={value =>
                                                    handleUpdateField(
                                                        selectedTransaction.id,
                                                        'notes',
                                                        value
                                                    )
                                                }
                                                placeholder="No notes"
                                                className="text-sm text-foreground"
                                                type="textarea"
                                                multiline={true}
                                                permissionModule={ModulePermission.TRANSACTIONS}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </DialogBody>

                            {/* Action Buttons */}
                            {canEditTransactions && (
                                <DialogFooter>
                                    {selectedTransaction.status === 'pending' && (
                                        <>
                                            <Button
                                                onClick={() =>
                                                    handleApproveTransaction(selectedTransaction.id)
                                                }
                                                variant="success"
                                            >
                                                <Check className="h-4 w-4" />
                                                Approve
                                            </Button>
                                            <Button
                                                onClick={() =>
                                                    handleRejectTransaction(selectedTransaction.id)
                                                }
                                                variant="destructive"
                                            >
                                                <X className="h-4 w-4" />
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        onClick={() =>
                                            handleDeleteTransaction(selectedTransaction.id)
                                        }
                                        variant="destructive"
                                    >
                                        <XCircle className="h-4 w-4" />
                                        Delete
                                    </Button>
                                </DialogFooter>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default TransactionsPage;
